import InputField from 'components/form-fields/formik/InputField';
import SelectField from 'components/form-fields/formik/SelectField';
import { FieldArray, FormikState } from 'formik';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  CustomerGroup,
  CustomerGroupFilterType,
  CustomerGroupFilterTypeValues,
} from 'subdomains/cpo/types/charging-policy';
import { DropdownOption } from 'subdomains/cpo/types/form';
import { Button, Header, Label, Message, Table } from 'semantic';
import { request } from 'utils/api';
import SearchDropdown from 'components/form-fields/formik/SearchDropdown';
import { Account } from 'types/account';
import { User } from 'types/user';
import { BillingPlan } from 'types/billingplan';
import DropdownField from 'components/form-fields/formik/DropdownField';
import { buildFormPath } from 'subdomains/cpo/utils/formpath';
import {
  createArrayFormItemWithId,
  useEnsureArrayFormItemIds,
} from 'utils/arrayformid';
import { CustomerGroupPreview } from './CustomerGroupPreview';
import { ActionButtonWithIcon } from 'components/semantic/ActionButtonWithIcon';

type Props = {
  basePath?: string;
  values: FormikState<CustomerGroup>['values'];
};

export const CustomerGroupFormFields = (props: Props) => {
  const { t } = useTranslation();

  const filtersWithTempIDs = useEnsureArrayFormItemIds(props.values?.filters);

  return (
    <>
      <Header
        as="h4"
        content={t(
          'providerAdmin.customerGroupForm.addGroupTitle',
          '1. Add group name and description'
        )}
      />
      <p>
        {t(
          'providerAdmin.customerGroupForm.addGroupDescription',
          'Add a unique name and a description to easily identify this group later.'
        )}
      </p>
      <InputField
        name="label"
        required
        label={t('providerAdmin.customerGroupForm.labelLabel', 'Name')}
      />
      <InputField
        name="description"
        label={t(
          'providerAdmin.customerGroupForm.descriptionLabel',
          'Description'
        )}
      />

      <Header
        as="h4"
        content={t(
          'providerAdmin.customerGroupForm.selectFiltersTitle',
          '2. Select the customers to group'
        )}
      />
      <p>
        {t(
          'providerAdmin.customerGroupForm.selectFiltersDescription',
          'Filter the customers by using categories or add the list of token UIDs.'
        )}
      </p>
      <FieldArray name="filters">
        {({ push, remove, replace }) => (
          <>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={4}>
                    {t(
                      'providerAdmin.customerGroupForm.filterCategory',
                      'Category'
                    )}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t(
                      'providerAdmin.customerGroupForm.filterSelectedValues',
                      'Selected values'
                    )}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtersWithTempIDs?.map((filter, index) => {
                  const usedTypes = filtersWithTempIDs
                    .map((filter, i) => (i !== index ? filter.type : undefined))
                    .filter(Boolean) as CustomerGroupFilterType[];

                  const tokenUIDOptions =
                    filter.values?.map((value) => ({
                      key: value,
                      value,
                      text: value,
                    })) ?? [];

                  return (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <Table.Row>
                          <Table.Cell
                            colSpan={3}
                            textAlign="center"
                            style={{
                              position: 'relative',
                              borderTop: 'none',
                              padding: '.2rem',
                            }}>
                            <Label
                              content={t(
                                'providerAdmin.customerGroupForm.orSeparator',
                                'OR'
                              )}
                              size="medium"
                              style={{
                                position: 'absolute',
                                top: '-0.5rem',
                                left: '21%',
                                height: '1.5rem',
                                padding: '0.4rem',
                              }}
                            />
                          </Table.Cell>
                        </Table.Row>
                      )}

                      <Table.Row>
                        <Table.Cell>
                          <SelectField
                            name={buildFormPath(
                              `filters[${index}].type`,
                              props.basePath
                            )}
                            options={getFilterTypeOptions(t, usedTypes)}
                            onChange={(e, { value }) => {
                              if (value !== filter.type) {
                                replace(index, {
                                  type: value,
                                  value: undefined,
                                });
                              }
                            }}
                            label={t(
                              'providerAdmin.customerGroupForm.filterByLabel',
                              'Filter by'
                            )}
                            required
                            style={{ width: 250 }}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          {filter?.type === CustomerGroupFilterType.ACCOUNT && (
                            <SearchDropdown
                              clearable
                              label={t(
                                'providerAdmin.customerGroupForm.accountsLabel',
                                'Accounts'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              objectMode={false}
                              multiple
                              onDataNeeded={(body: Object) => {
                                return request<{ data: Account[] }>({
                                  path: '/1/accounts/search',
                                  method: 'POST',
                                  body: body,
                                });
                              }}
                            />
                          )}
                          {filter?.type === CustomerGroupFilterType.USER && (
                            <SearchDropdown
                              clearable
                              label={t(
                                'providerAdmin.customerGroupForm.usersLabel',
                                'Users'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              objectMode={false}
                              multiple
                              onDataNeeded={(body: Object) => {
                                return request<{ data: User[] }>({
                                  path: '/1/users/search',
                                  method: 'POST',
                                  body: body,
                                });
                              }}
                            />
                          )}
                          {filter?.type ===
                            CustomerGroupFilterType.CONSUMER_CARD_BILLING_PLAN && (
                            <SearchDropdown
                              clearable
                              label={t(
                                'providerAdmin.customerGroupForm.billingPlansLabel',
                                'Card Billing Plans'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              objectMode={false}
                              multiple
                              onDataNeeded={(body: Object) => {
                                return request<{ data: BillingPlan[] }>({
                                  path: '/1/billing-plans/search',
                                  method: 'POST',
                                  body: body,
                                });
                              }}
                            />
                          )}
                          {filter?.type ===
                            CustomerGroupFilterType.INFRA_TOKEN_PROVIDER && (
                            <DropdownField
                              label={t(
                                'providerAdmin.customerGroupForm.infraTokenProvidersLabel',
                                'Infra Token Providers'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              fluid
                              multiple
                              search
                              allowAdditions
                              selection
                              options={[]}
                            />
                          )}
                          {filter?.type === CustomerGroupFilterType.UID && (
                            <DropdownField
                              label={t(
                                'providerAdmin.customerGroupForm.tokenUidsLabel',
                                'Token UIDs'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              fluid
                              multiple
                              search
                              allowAdditions
                              selection
                              options={tokenUIDOptions}
                            />
                          )}
                          {filter?.type ===
                            CustomerGroupFilterType.ISSUER_NAME && (
                            <DropdownField
                              label={t(
                                'providerAdmin.customerGroupForm.issuerNamesLabel',
                                'Issuer Names'
                              )}
                              name={buildFormPath(
                                `filters[${index}].values`,
                                props.basePath
                              )}
                              fluid
                              multiple
                              search
                              allowAdditions
                              selection
                              options={[]}
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            icon="trash"
                            basic
                            disabled={filtersWithTempIDs.length === 1}
                            size="mini"
                            onClick={(
                              e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              e.preventDefault();
                              remove(index);
                            }}
                            style={{ marginTop: 22, height: 20, width: 20 }}
                          />
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell
                          colSpan={3}
                          style={{ borderTop: 'none', paddingTop: '0' }}>
                          <p
                            style={{ marginTop: '.4rem', fontStyle: 'italic' }}>
                            {filter.type === CustomerGroupFilterType.ACCOUNT &&
                              t(
                                'providerAdmin.customerGroupForm.accountFilterDescription',
                                'Any token under the selected accounts will be automatically associated with this group.'
                              )}
                            {filter.type === CustomerGroupFilterType.USER &&
                              t(
                                'providerAdmin.customerGroupForm.userFilterDescription',
                                'Any token under the selected users will be automatically associated with this group.'
                              )}
                            {filter.type ===
                              CustomerGroupFilterType.CONSUMER_CARD_BILLING_PLAN &&
                              t(
                                'providerAdmin.customerGroupForm.consumerCardBillingPlanFilterDescription',
                                'Any token that has one of the selected card billing plans applied, will be automatically associated with this group.'
                              )}
                            {filter.type ===
                              CustomerGroupFilterType.INFRA_TOKEN_PROVIDER &&
                              t(
                                'providerAdmin.customerGroupForm.infraTokenProviderFilterDescription',
                                'Any token that is provided by one of the selected infra token providers will be automatically associated with this group.'
                              )}
                            {filter.type === CustomerGroupFilterType.UID && (
                              <Message warning>
                                {t(
                                  'providerAdmin.customerGroupForm.uidFilterDescriptionWarning',
                                  'The selected token UIDs will be associated with this group. If any tokens are unrecognized, they will only be accepted for charging when the associated tariffs are configured with no cost.'
                                )}
                              </Message>
                            )}
                            {filter.type ===
                              CustomerGroupFilterType.ISSUER_NAME &&
                              t(
                                'providerAdmin.customerGroupForm.issuerNameFilterDescription',
                                'Any token that is provided by one of the selected issuer names will be automatically associated with this group.'
                              )}
                            {!filter.type &&
                              t(
                                'providerAdmin.customerGroupForm.noFilterSelectedDescription',
                                'Please select a filter to associate tokens with this group.'
                              )}
                          </p>
                        </Table.Cell>
                      </Table.Row>
                    </React.Fragment>
                  );
                })}
              </Table.Body>
            </Table>
            <div style={{ marginLeft: 10, paddingBottom: 20 }}>
              <ActionButtonWithIcon
                text={t(
                  'providerAdmin.customerGroupForm.addFilterButton',
                  'Add another filter'
                )}
                icon="plus"
                onClick={() => push(createArrayFormItemWithId({}))}
              />
            </div>
          </>
        )}
      </FieldArray>

      <Header
        as="h4"
        content={t(
          'providerAdmin.customerGroupForm.reviewCustomerGroupTitleStep',
          '3. Review customer group'
        )}
      />
      <p>
        {t(
          'providerAdmin.customerGroupForm.reviewCustomerGroupDescription',
          'Preview how the group will be created'
        )}
      </p>
      <CustomerGroupPreview values={props.values} />
    </>
  );
};

const getFilterTypeOptions = (
  t: TFunction<'translation', undefined>,
  usedTypes: CustomerGroupFilterType[] = []
): DropdownOption<CustomerGroupFilterType>[] => {
  return CustomerGroupFilterTypeValues.filter(
    (value) =>
      !usedTypes.includes(value) &&
      // TODO: remove this filter once all customer group filters have been implemented
      value === CustomerGroupFilterType.UID
  ).map((value) => ({
    key: value,
    value,
    text: getFilterTypeLabel(t, value),
  }));
};

const getFilterTypeLabel = (
  t: TFunction<'translation', undefined>,
  type: CustomerGroupFilterType
): string => {
  switch (type) {
    case CustomerGroupFilterType.ACCOUNT:
      return t(
        'providerAdmin.customerGroupForm.filterTypeAccountOption',
        'Account'
      );
    case CustomerGroupFilterType.USER:
      return t('providerAdmin.customerGroupForm.filterTypeUserOption', 'User');
    case CustomerGroupFilterType.CONSUMER_CARD_BILLING_PLAN:
      return t(
        'providerAdmin.customerGroupForm.filterTypeConsumerCardBillingPlanOption',
        'Card Billing Plan'
      );
    case CustomerGroupFilterType.INFRA_TOKEN_PROVIDER:
      return t(
        'providerAdmin.customerGroupForm.filterTypeInfraTokenProviderOption',
        'Infra Token Provider'
      );
    case CustomerGroupFilterType.UID:
      return t(
        'providerAdmin.customerGroupForm.filterTypeTokenUidOption',
        'Token UID'
      );
    case CustomerGroupFilterType.ISSUER_NAME:
      return t(
        'providerAdmin.customerGroupForm.filterTypeIssuerNameOption',
        'Issuer Name'
      );
    default:
      return '';
  }
};
